import { Tag } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { OrderSummary } from "flows/Picking/components/OrderSummary";
import { OrderDetails, PickingState } from "flows/Picking/models/order/types";
import { getEmployeeName, getPickingStateConfig } from "flows/Picking/utils/pickingDetailsHelpers";
import { useWaitingOrdersSound } from "shared/hooks/useWaitingOrdersSound";
import { Button } from "ui/Button/Button";
import { BodyS, HeaderS, TitleXS } from "ui/Typography/Typography";
import { isNotNullNorUndefined } from "utils/tsHelpers";

import { TableLine } from "../TableLine";

type NewOrderDetailsProps = {
  orderDetails: OrderDetails;
  onStartPicking: () => void;
  onConfirmCancelled: () => void;
  isStartPickingLoading: boolean;
};

export function NewOrderDetails({
  orderDetails,
  onStartPicking,
  onConfirmCancelled,
  isStartPickingLoading,
}: NewOrderDetailsProps) {
  const intl = useIntl();
  useWaitingOrdersSound();

  const { pickingState } = orderDetails;
  const stateConfig = pickingState ? getPickingStateConfig(pickingState) : null;

  return (
    <>
      <HeaderS color="pinkFlink.500">
        <FormattedMessage id="components.picking.new-order-summary.new-order-title" />
      </HeaderS>

      <OrderSummary order={{ ...orderDetails, items: [] }} state="START" />

      {pickingState === PickingState.PICKING_STATE_IN_PROGRESS &&
        isNotNullNorUndefined(orderDetails.startedBy) && (
          <TableLine
            label={intl.formatMessage({ id: "components.picking.order-details.started-by" })}
            rightElem={getEmployeeName(orderDetails.startedBy)}
          />
        )}

      {pickingState === PickingState.PICKING_STATE_COMPLETED && (
        <>
          {isNotNullNorUndefined(orderDetails.finishedBy) && (
            <TableLine
              label={intl.formatMessage({ id: "components.picking.order-details.finished-by" })}
              rightElem={getEmployeeName(orderDetails.finishedBy)}
            />
          )}

          {orderDetails.handoverContainerIds.length > 0 && (
            <TableLine
              label={intl.formatMessage({ id: "components.picking.order-details.containers" })}
              height="fit-content"
              rightElem={
                <BodyS my="s200" width="50%" align="end" wordBreak="break-word">
                  {orderDetails.handoverContainerIds.map((value) => value.slice(-5)).join(", ")}
                </BodyS>
              }
            />
          )}

          {orderDetails.handoverShelfNumbers.length > 0 && (
            <TableLine
              label={intl.formatMessage({ id: "components.picking.order-details.shelves" })}
              rightElem={
                <BodyS my="s200" width="50%" align="end" wordBreak="break-word">
                  {orderDetails.handoverShelfNumbers.join(", ")}
                </BodyS>
              }
            />
          )}
        </>
      )}

      {stateConfig && (
        <TableLine
          label={intl.formatMessage({ id: "components.picking.order-details.picking-state" })}
          rightElem={
            <Tag bg={stateConfig.bg} borderRadius="4px">
              <TitleXS color="white">{intl.formatMessage({ id: stateConfig.messageId })}</TitleXS>
            </Tag>
          }
        />
      )}

      {pickingState !== PickingState.PICKING_STATE_CANCELLED && (
        <Button
          mt="s200"
          isLoading={isStartPickingLoading}
          onClick={onStartPicking}
          isFullWidth
          data-testid="button-start-picking"
          size="lg"
        >
          <FormattedMessage id="components.picking.new-order-summary.start-picking" />
        </Button>
      )}

      {pickingState === PickingState.PICKING_STATE_CANCELLED && (
        <Button
          mt="s200"
          isLoading={isStartPickingLoading}
          onClick={onConfirmCancelled}
          isFullWidth
          data-testid="button-confirm-cancelled"
          size="lg"
        >
          <FormattedMessage id="components.picking.conflict-modal.close" />
        </Button>
      )}
    </>
  );
}
